import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Chip,
  Checkbox,
  IconButton,
} from '@mui/material';
import {
  ArrowBack,
  Search,
  FilterList,
  CheckCircle,
  TrendingUp,
  ErrorOutline,
  PersonAddAlt,
  DoneAll,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { mockNotifications, NOTIFICATION_TYPES } from './notificationData';

const typeIconMap = {
  [NOTIFICATION_TYPES.SUCCESS]: { icon: <TrendingUp sx={{ fontSize: 22 }} />, bg: '#1B3D1B', color: '#4CAF50' },
  [NOTIFICATION_TYPES.WARNING]: { icon: <ErrorOutline sx={{ fontSize: 22 }} />, bg: '#3D2E00', color: '#E1AD01' },
  [NOTIFICATION_TYPES.INFO]: { icon: <CheckCircle sx={{ fontSize: 22 }} />, bg: '#1B3D1B', color: '#4CAF50' },
  [NOTIFICATION_TYPES.USER]: { icon: <PersonAddAlt sx={{ fontSize: 22 }} />, bg: '#2A1A3D', color: '#9C7BDB' },
};

const typeBadgeColors = {
  [NOTIFICATION_TYPES.SUCCESS]: { bg: '#1B3D1B', color: '#4CAF50' },
  [NOTIFICATION_TYPES.WARNING]: { bg: '#3D2E00', color: '#E1AD01' },
  [NOTIFICATION_TYPES.INFO]: { bg: '#1A2A3A', color: '#64B5F6' },
  [NOTIFICATION_TYPES.USER]: { bg: '#2A1A3D', color: '#9C7BDB' },
};

const FILTER_TABS = ['All', 'Success', 'Warning', 'Info', 'User'];

const Notifications = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState(mockNotifications);
  const [activeFilter, setActiveFilter] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);

  const unreadCount = notifications.filter((n) => !n.read).length;

  const filteredNotifications = useMemo(() => {
    let filtered = notifications;
    if (activeFilter !== 'All') {
      filtered = filtered.filter((n) => n.type === activeFilter);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (n) => n.title.toLowerCase().includes(q) || n.description.toLowerCase().includes(q)
      );
    }
    return filtered;
  }, [notifications, activeFilter, searchQuery]);

  const filterCounts = useMemo(() => {
    const counts = { All: notifications.length };
    Object.values(NOTIFICATION_TYPES).forEach((type) => {
      counts[type] = notifications.filter((n) => n.type === type).length;
    });
    return counts;
  }, [notifications]);

  // Group by date
  const groupedNotifications = useMemo(() => {
    const groups = {};
    filteredNotifications.forEach((n) => {
      if (!groups[n.date]) groups[n.date] = [];
      groups[n.date].push(n);
    });
    return groups;
  }, [filteredNotifications]);

  const handleMarkAllRead = () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedIds(filteredNotifications.map((n) => n.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (id, checked) => {
    setSelectedIds((prev) => (checked ? [...prev, id] : prev.filter((i) => i !== id)));
  };

  const allSelected =
    filteredNotifications.length > 0 && filteredNotifications.every((n) => selectedIds.includes(n.id));

  return (
    <Box sx={{ backgroundColor: '#000000', minHeight: '100%', width: '100%' }}>
      {/* Back Button */}
      <Box
        onClick={() => navigate('/dashboard')}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 1,
          cursor: 'pointer',
          mb: 2,
          '&:hover': { opacity: 0.8 },
        }}
      >
        <Box
          sx={{
            width: 34,
            height: 34,
            borderRadius: '50%',
            backgroundColor: '#E1AD01',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <ArrowBack sx={{ fontSize: 18, color: '#000000' }} />
        </Box>
        <Typography sx={{ color: '#AAAAAA', fontSize: 14 }}>Back to Dashboard</Typography>
      </Box>

      {/* Header Row */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 1,
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        <Typography variant="h4" sx={{ color: '#FFFFFF', fontWeight: 700, fontSize: { xs: 24, md: 30 } }}>
          All Notifications
        </Typography>
        <Button
          variant="contained"
          onClick={handleMarkAllRead}
          startIcon={<DoneAll sx={{ fontSize: 18 }} />}
          sx={{
            backgroundColor: '#E1AD01',
            color: '#000000',
            fontWeight: 600,
            borderRadius: '24px',
            textTransform: 'none',
            px: 3,
            py: 1,
            '&:hover': { backgroundColor: '#E6B800' },
          }}
        >
          Mark all as read
        </Button>
      </Box>

      {/* Subtitle */}
      <Typography sx={{ color: '#AAAAAA', fontSize: 14, mb: 3 }}>
        You have <span style={{ color: '#E1AD01', fontWeight: 600 }}>{unreadCount} unread</span> notifications
        out of {notifications.length} total
      </Typography>

      {/* Search & Filter Card */}
      <Box
        sx={{
          backgroundColor: 'rgba(26, 26, 26, 1)',
          borderRadius: '16px',
          p: 2.5,
          mb: 3,
        }}
      >
        {/* Search Row */}
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <TextField
            placeholder="Search notifications..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            size="small"
            fullWidth
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'rgba(42, 42, 42, 1)',
                borderRadius: '12px',
                color: '#FFFFFF',
                '& fieldset': { borderColor: '#404040' },
                '&:hover fieldset': { borderColor: '#666666' },
                '&.Mui-focused fieldset': { borderColor: '#E1AD01' },
              },
              '& .MuiInputBase-input::placeholder': { color: '#888888' },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ color: '#888888', fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
          />
          <IconButton
            sx={{
              border: '1px solid #404040',
              borderRadius: '12px',
              color: '#888888',
              px: 2,
              '&:hover': { borderColor: '#666666' },
            }}
          >
            <FilterList sx={{ fontSize: 20 }} />
            <Typography sx={{ fontSize: 13, ml: 0.5 }}>Filter:</Typography>
          </IconButton>
        </Box>

        {/* Filter Tabs */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {FILTER_TABS.map((tab) => {
            const isActive = activeFilter === tab;
            const count = filterCounts[tab] || 0;
            return (
              <Chip
                key={tab}
                onClick={() => setActiveFilter(tab)}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                    <span>{tab}</span>
                    <Box
                      sx={{
                        backgroundColor: isActive ? 'rgba(0,0,0,0.2)' : 'rgba(255,255,255,0.1)',
                        borderRadius: '10px',
                        px: 0.8,
                        py: 0.1,
                        fontSize: 11,
                        fontWeight: 700,
                        minWidth: 20,
                        textAlign: 'center',
                        lineHeight: '18px',
                      }}
                    >
                      {count}
                    </Box>
                  </Box>
                }
                sx={{
                  backgroundColor: isActive ? '#E1AD01' : 'rgba(255,255,255,0.08)',
                  color: isActive ? '#000000' : '#CCCCCC',
                  fontWeight: 600,
                  fontSize: 13,
                  borderRadius: '20px',
                  cursor: 'pointer',
                  height: 34,
                  '&:hover': {
                    backgroundColor: isActive ? '#E6B800' : 'rgba(255,255,255,0.12)',
                  },
                }}
              />
            );
          })}
        </Box>
      </Box>

      {/* Select All */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Checkbox
          checked={allSelected}
          onChange={(e) => handleSelectAll(e.target.checked)}
          sx={{
            color: '#666666',
            '&.Mui-checked': { color: '#E1AD01' },
            p: 0,
            mr: 1,
          }}
        />
        <Typography sx={{ color: '#AAAAAA', fontSize: 14 }}>Select all</Typography>
      </Box>

      {/* Notification Groups */}
      {Object.entries(groupedNotifications).map(([date, items]) => (
        <Box key={date} sx={{ mb: 3 }}>
          {/* Date Header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              mb: 2,
            }}
          >
            <Typography sx={{ color: '#E1AD01', fontWeight: 700, fontSize: 15, whiteSpace: 'nowrap' }}>
              {date}
            </Typography>
            <Box sx={{ flex: 1, height: '1px', backgroundColor: '#E1AD01', opacity: 0.3 }} />
            <Typography sx={{ color: '#888888', fontSize: 12, whiteSpace: 'nowrap' }}>
              {items.length} notification{items.length !== 1 ? 's' : ''}
            </Typography>
          </Box>

          {/* Notification Cards */}
          {items.map((notification) => {
            const typeInfo = typeIconMap[notification.type];
            const badgeColors = typeBadgeColors[notification.type];
            const isSelected = selectedIds.includes(notification.id);

            return (
              <Box
                key={notification.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  backgroundColor: 'rgba(26, 26, 26, 1)',
                  borderRadius: '14px',
                  px: 2.5,
                  py: 2,
                  mb: 1.5,
                  border: '1px solid',
                  borderColor: isSelected ? '#E1AD01' : '#2A2A2A',
                  transition: 'border-color 0.2s',
                  '&:hover': {
                    backgroundColor: 'rgba(35, 35, 35, 1)',
                    borderColor: '#404040',
                  },
                }}
              >
                {/* Checkbox */}
                <Checkbox
                  checked={isSelected}
                  onChange={(e) => handleSelectOne(notification.id, e.target.checked)}
                  sx={{
                    color: '#666666',
                    '&.Mui-checked': { color: '#E1AD01' },
                    p: 0,
                  }}
                />

                {/* Icon */}
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: '50%',
                    backgroundColor: typeInfo.bg,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: typeInfo.color,
                    flexShrink: 0,
                  }}
                >
                  {typeInfo.icon}
                </Box>

                {/* Content */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography sx={{ fontWeight: 600, fontSize: 15, color: '#FFFFFF' }}>
                      {notification.title}
                    </Typography>
                    {!notification.read && (
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          backgroundColor: '#E1AD01',
                          flexShrink: 0,
                        }}
                      />
                    )}
                    <Chip
                      label={notification.type}
                      size="small"
                      sx={{
                        backgroundColor: badgeColors.bg,
                        color: badgeColors.color,
                        fontWeight: 600,
                        fontSize: 11,
                        height: 22,
                        borderRadius: '6px',
                      }}
                    />
                  </Box>
                  <Typography
                    sx={{
                      fontSize: 13,
                      color: '#AAAAAA',
                      mt: 0.5,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {notification.description}
                  </Typography>
                </Box>

                {/* Time */}
                <Typography
                  sx={{
                    fontSize: 12,
                    color: '#888888',
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                  }}
                >
                  {notification.time}
                </Typography>
              </Box>
            );
          })}
        </Box>
      ))}

      {/* Empty State */}
      {filteredNotifications.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography sx={{ color: '#888888', fontSize: 16 }}>No notifications found</Typography>
        </Box>
      )}
    </Box>
  );
};

export default Notifications;
